import React, {
    Dispatch,
    forwardRef,
    SetStateAction,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
} from 'react';
import { Redirect } from 'react-router-dom';

import {
    addDirectedTreeIcons,
    locale,
    summarizeDirectedTree,
} from '@trqk/utils';

import { RequestInvitationTab } from '..';
import { useDashboard, useToast } from '../../../../../hooks';
import { StatementsChartsContent } from './StatementsChartsContent';
import { PATHS } from '../../../../../constants';
import { summarizeDirectedTreeConfig } from './constants';
import { hideExplorerSection, showExplorerSection } from './helpers';
import { getFeatureFlags } from '../../../../../featureFlags';

interface StatementsChartsPreviewProps {
    setSectionConfigs: Dispatch<SetStateAction<HeaderSectionConfig[]>>;
    setShowMediaExplorer: SetState<boolean>;
}

/**
 * Displays a series of charts for the data in a single PRO statement.
 *
 * This component only fetches the dashboard data; the StatementsChartsContent component renders the
 * content of the page.
 */
export const StatementsChartsPreview = forwardRef(
    (
        {
            setSectionConfigs,
            setShowMediaExplorer,
        }: StatementsChartsPreviewProps,
        ref,
    ): JSX.Element | null => {
        const { truncateChartData } = getFeatureFlags();
        const { add: addToast } = useToast();
        const { dashboard, dashboardIsUnavailable } = useDashboard();
        const [loadingMediaExplorerData, setLoadingMediaExplorerData] =
            useState(true);
        const mediaExplorerData = useMemo(() => {
            const directedTree = dashboard?.directed_tree ?? null;
            const nextMediaExplorerData = truncateChartData
                ? summarizeDirectedTree({
                      directedTree,
                      config: summarizeDirectedTreeConfig,
                  })
                : addDirectedTreeIcons({
                      directedTree,
                      config: summarizeDirectedTreeConfig,
                  });

            return nextMediaExplorerData;
        }, [dashboard]);

        useLayoutEffect(() => {
            if (mediaExplorerData === null) {
                setSectionConfigs(
                    (prev: HeaderSectionConfig[]): HeaderSectionConfig[] =>
                        prev.map(hideExplorerSection),
                );
            } else {
                setSectionConfigs(
                    (prev: HeaderSectionConfig[]): HeaderSectionConfig[] =>
                        prev.map(showExplorerSection),
                );
            }
            setLoadingMediaExplorerData(false);
        }, [mediaExplorerData, setSectionConfigs]);

        if (dashboardIsUnavailable) {
            addToast({
                kind: 'warning-banner',
                content: locale.enUS.TRYOUT.DASHBOARD_IS_UNAVAILABLE,
            });

            return <Redirect to={PATHS.TRYOUT} />;
        }

        const showMediaExplorer = mediaExplorerData !== null;

        useEffect(() => {
            setShowMediaExplorer(showMediaExplorer);
        }, [showMediaExplorer]);

        const statementId = dashboard?.statement_info?.statement_id;

        if (dashboard === null || !statementId) {
            return null;
        }

        return (
            <>
                <StatementsChartsContent
                    dashboard={dashboard as ProIngestionDashboard}
                    id={statementId}
                    loadingMediaExplorerData={loadingMediaExplorerData}
                    ref={ref}
                    mediaExplorerData={mediaExplorerData}
                    showMediaExplorer={mediaExplorerData !== null}
                />
                <RequestInvitationTab statementId={statementId} />
            </>
        );
    },
);

StatementsChartsPreview.displayName = 'StatementsChartsPreview';
